/**
 * Shared mapping from MCP module names to frontend module config IDs.
 * Only includes entries where the names differ.
 */
export const MCP_TO_MODULE_ID: Record<string, string> = {
  ai_agents: 'ai',
  user_management: 'admin',
  file_management: 'files',
};

/**
 * Reverse mapping: frontend module config ID → backend MCP module name.
 * Only includes entries where the names differ.
 */
export const MODULE_ID_TO_MCP: Record<string, string> = {
  ai: 'ai_agents',
  admin: 'user_management',
  files: 'file_management',
};

/**
 * Legacy MCP module names → current runtime keys. Between 2026-05 and the
 * WI 0ba39ef9 fix, master_data's toolsets were registered under dotted keys;
 * chat sessions configured in that window may still carry them in their
 * saved tool config. Runtime keys are always undotted (they prefix Anthropic
 * tool names, which reject dots).
 */
const LEGACY_MCP_MODULE_ALIASES: Record<string, string> = {
  'master_data.products': 'products',
  'master_data.contacts': 'contacts',
};

/**
 * Map a legacy dotted MCP module name to its current runtime key.
 * Pass-through for everything else.
 */
export function normalizeLegacyMcpModuleName(mcpModuleName: string): string {
  return LEGACY_MCP_MODULE_ALIASES[mcpModuleName] ?? mcpModuleName;
}

export function normalizeMcpModuleNames(mcpModuleNames: string[]): string[] {
  return [...new Set(mcpModuleNames.map(normalizeLegacyMcpModuleName))];
}

/**
 * Resolve an MCP module name to a frontend module config ID.
 */
export function resolveModuleId(mcpModuleName: string): string {
  return MCP_TO_MODULE_ID[mcpModuleName] || mcpModuleName;
}

/**
 * Resolve a frontend module config ID to a backend MCP module name.
 */
export function resolveMcpModuleName(moduleId: string): string {
  return MODULE_ID_TO_MCP[moduleId] || moduleId;
}

/**
 * Frontend module config ID → the backend MCP module name(s) whose tools the
 * global Luma assistant enables when its side panel is opened on that module.
 *
 * This is the SINGLE SOURCE for two things GlobalAIButton needs: which UI
 * modules have an in-app Luma assistant (enhanced mode → model picker + MCP
 * config), and which MCP toolset(s) back each one. It is keyed by CURRENT
 * frontend module config ids (see `modules_entities.config.json`) — NOT backend
 * MCP names — so a module rename or consolidation surfaces here (and in its
 * drift-guard test) instead of silently dropping Luma into basic mode.
 *
 * `master_data` is the one UI module that spans two backend MCP domains
 * (products + contacts), so it enables both. Values must match a registered
 * backend chat provider / MCP module name; keys must be real module ids.
 */
export const MODULE_AI_MCP: Record<string, string[]> = {
  tasks: ['tasks'],
  pipeline: ['pipeline'],
  recruiting: ['recruiting'],
  accounting: ['accounting'],
  support: ['support'],
  master_data: ['products', 'contacts'],
  people: ['people'],
  ai: ['ai_agents'],
  ai_chat: ['ai_chat'],
  forms_bpm: ['forms_bpm'],
  website_hosting: ['website_hosting'],
  email: ['email'],
  messaging: ['messaging'],
  files: ['file_management'],
  software_factory: ['software_factory'],
  inventory: ['inventory'],
  marketing: ['marketing'],
  contracts: ['contracts'],
  sales: ['sales'],
  shipments: ['shipments'],
  procurement: ['procurement'],
  billing: ['billing'],
  chatbot: ['chatbot'],
  developer: ['developer'],
};

/**
 * The backend MCP module name(s) Luma should enable for a frontend module, or
 * `[]` if the module has no in-app assistant.
 */
export function getModuleAiMcpModules(moduleId: string): string[] {
  return MODULE_AI_MCP[moduleId] ?? [];
}

/** Whether the given frontend module has an in-app Luma assistant. */
export function moduleHasAiAssistant(moduleId: string): boolean {
  return moduleId in MODULE_AI_MCP;
}
